import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Modal, Grid, GridCol, Select, Table, TextInput, Loader } from '@mantine/core';
import ModalComponent from '../../../components/modal';
import PaginationComponent from '../../../components/pagination';
import { LoginState } from '../../../client-redux/login/reducer';
import { AppState } from '../../../client-redux/reducers';
import { Language } from '../../../languages/languageHandler';
import { IHelpdeskEntity, fetchHelpdeskEntitiesList } from '../../../../libraries/helpdesk/entities';
import { entityTypes } from '../../utils';
import { useStatusOperDialog, isZarphSuccessResponse } from '../../../context/status-oper-dialog';
import './styles.scss';

interface ModalProps {
  isOpen: boolean;
  hideBtn: () => void;
  lang: Language;
  chooseEntid: (entid: number, name?: string) => void;
}

const ChooseEntityModal = ({ isOpen, hideBtn, lang, chooseEntid }: ModalProps) => {
  const loginInfo = useSelector<AppState, LoginState>(state => state.login);
  const controller = useRef(new AbortController()).current;

  const [type, setType] = useState<string>('');
  const [entid, setEntid] = useState<number>(0);
  const [name, setName] = useState<string>('');
  const [nif, setNif] = useState<number>(0);
  const [offset, setOffset] = useState<number>(0);
  const [limit, setLimit] = useState<number>(10);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [message, setMessage] = useState<IHelpdeskEntity[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [lastSelectedPage, setLastSelectedPage] = useState<number>(1);
  const [firstLoad, setFirstLoad] = useState<boolean>(true);

  const { handleApiError } = useStatusOperDialog();

  useEffect(() => {
    if (!isOpen) {
      setType('');
      setEntid(0);
      setName('');
      setNif(0);
      setOffset(0);
      setLimit(10);
      setMessage([]);
      setCurrentPage(1);
      setLastSelectedPage(1);
      setFirstLoad(true);
    }
  }, [isOpen]);

  const getEntityList = () => {
    if (!loginInfo.token) {
      return;
    }

    setLoading(true);
    setMessage([]);
    setTotalRows(0);

    fetchHelpdeskEntitiesList(controller, offset, limit, true, type, entid, name, nif)
      .then(response => {
        if (isZarphSuccessResponse(response, [9004])) {
          setMessage(response?.data?.entities || []);
          setTotalRows(response?.data?.totalRows || 0);
          setCurrentPage(lastSelectedPage);
        } else {
          console.error('Error response:', response);
          handleApiError(response, lang.ALERT_ERROR_FETCHING_DATA);
          setLoading(false);
        }
      })
      .catch(err => {
        // unexpected errors go here
        handleApiError(err, lang.ALERT_ERROR_FETCHING_DATA);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (firstLoad) {
      setFirstLoad(false);
    } else {
      getEntityList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset, limit, currentPage]);

  const changePage = (page: number) => {
    const newOffset = (page - 1) * limit;
    setCurrentPage(page);
    setOffset(newOffset);
    setLastSelectedPage(page);
  };

  const resultsSelect = (newValue: number) => {
    const newLimit = Number(newValue);
    setLimit(newLimit);
    setCurrentPage(0);
    setOffset(0);
    setCurrentPage(1);
  };

  /* Entity handle*/
  const handleEntity = (value: number) => {
    if (value !== null) {
      const selectedEntid = Number(value);
      setEntid(selectedEntid);
    }
  };

  /* Name handle*/
  const handleName = (value: string) => {
    if (value !== null) {
      const selectedName = String(value);
      setName(selectedName);
    }
  };

  /* NIF handle*/
  const handleNIF = (value: number) => {
    if (value !== null) {
      const selectedNif = Number(value);
      setNif(selectedNif);
    }
  };

  /* Type handle*/
  const handleNewTableByType = (value: string | 0) => {
    if (value !== null) {
      const selectedType = String(value);
      setType(selectedType!);
    }
  };

  const handleEntitySelection = (entid: number, name: string) => {
    chooseEntid(entid, name);
    hideBtn();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      getEntityList();
    }
  };

  return (
    <ModalComponent
      show={isOpen}
      handleClick={hideBtn}
      title={lang.HELPDESK_CHOOSE_ENTITY}
      classname="choose-entity-modal"
      content={
        <Modal.Body style={{ margin: 0, padding: '1vw 1vh' }}>
          <Grid classNames={{ root: 'choose-entity' }}>
            {/* Filters */}
            <GridCol span={6}>
              <TextInput label="Entid" onChange={e => handleEntity(Number(e.target.value))} onKeyDown={handleKeyDown} />
            </GridCol>

            <GridCol span={6}>
              <TextInput
                label={lang.GLOBAL_NAME}
                onChange={e => handleName(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </GridCol>

            <GridCol span={6}>
              <TextInput label="NIF" onChange={e => handleNIF(Number(e.target.value))} onKeyDown={handleKeyDown} />
            </GridCol>

            <GridCol span={6}>
              <Select
                label={lang.GLOBAL_TYPE}
                data={entityTypes(lang)}
                placeholder={lang.GLOBAL_ALL}
                onChange={value => handleNewTableByType(String(value))}
              />
            </GridCol>

            {/* Search button */}
            <GridCol span={12}>
              <Button radius="md" onClick={getEntityList} loading={loading}>
                {lang.GLOBAL_SEARCH}
              </Button>
            </GridCol>

            {/* Table */}
            {message && (
              <>
                <GridCol span={12}>
                  <Table.ScrollContainer minWidth={500}>
                    <Table striped highlightOnHover className="entity-table">
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Entid</Table.Th>
                          <Table.Th>{lang.GLOBAL_NAME}</Table.Th>
                        </Table.Tr>
                      </Table.Thead>

                      <Table.Tbody>
                        {loading ? (
                          <Table.Tr>
                            <Table.Td colSpan={2} style={{ textAlign: 'center' }}>
                              <Loader />
                            </Table.Td>
                          </Table.Tr>
                        ) : !message?.length ? (
                          <Table.Tr>
                            <Table.Td colSpan={2} style={{ textAlign: 'center' }}>
                              {lang.GLOBAL_NO_RESULTS}
                            </Table.Td>
                          </Table.Tr>
                        ) : (
                          message.map((entity, index) => (
                            <Table.Tr
                              key={index}
                              onClick={() => handleEntitySelection(entity.entid || 0, entity.name || '')}
                              style={{ cursor: 'pointer' }}
                            >
                              <Table.Td>{entity.entid}</Table.Td>
                              <Table.Td>{entity.name}</Table.Td>
                            </Table.Tr>
                          ))
                        )}
                      </Table.Tbody>
                    </Table>
                  </Table.ScrollContainer>
                </GridCol>

                <PaginationComponent
                  total={totalRows}
                  itemsPerPage={limit}
                  dynamicLimit={limit}
                  currentPage={currentPage}
                  onPageChange={changePage}
                  onResultsChange={resultsSelect}
                />
              </>
            )}
          </Grid>
        </Modal.Body>
      }
    />
  );
};

export default ChooseEntityModal;
